import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RoleFeatures } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  featureKey?: keyof RoleFeatures;
}

export default function ProtectedRoute({ children, requireAdmin, featureKey }: ProtectedRouteProps) {
  const { user, token, loading, canAccess } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!token || !user) {
    return <Navigate to={`/login?from=${encodeURIComponent(location.pathname)}`} state={{ from: location }} replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <p className="text-amber-800 font-medium">Access denied</p>
          <p className="text-amber-700 text-sm mt-1">This page is for administrators only.</p>
        </div>
      </div>
    );
  }

  if (featureKey && !canAccess(featureKey)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
